import React, { useState, useEffect, useCallback } from 'react';
import {
  FiFileText,
  FiSearch,
  FiFilter,
  FiRefreshCw,
  FiAlertCircle
} from 'react-icons/fi';
import { toast } from 'react-hot-toast';
import { AdminLayout } from '../../components/admin';
import {
  ClaimCard,
  ApproveClaimModal,
  RejectClaimModal,
  type Claim
} from '../../components/admin/claims';
import ViewClaimDetailsModal from '../../components/admin/claims/ViewClaimDetailsModal';
import { useAdminAuth } from '../../hooks/admin/useAdminAuth';
import { useErrorHandler } from '../../hooks/useErrorHandler';
import { API_URL } from '../../services/api';

// Types
type TabStatus = 'pending' | 'approved' | 'rejected';

interface ClaimsResponse {
  claims: Claim[];
  total: number;
  pending: number;
  approved: number;
  rejected: number;
}

const RequestsPage: React.FC = () => {
  const { token } = useAdminAuth();
  const [errorState, { handleError, clearError }] = useErrorHandler();

  // Custom API call function
  const callApi = useCallback(
    async <T = any>(options: {
      method: 'GET' | 'POST' | 'PUT' | 'DELETE';
      endpoint: string;
      data?: any;
    }): Promise<{ success: boolean; data: T; message?: string }> => {
      if (!token) {
        throw new Error('ไม่พบ token สำหรับการยืนยันตัวตน');
      }

      
      const response = await fetch(`${API_URL}${options.endpoint}`, {
        method: options.method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: options.data ? JSON.stringify(options.data) : undefined
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || result.detail || `HTTP ${response.status}`);
      }

      return {
        success: true,
        data: result.data || result,
        message: result.message
      };
    },
    [token]
  );

  // State
  const [claims, setClaims] = useState<Claim[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalClaims, setTotalClaims] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [activeTab, setActiveTab] = useState<TabStatus>('pending');
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  // Modal states
  const [approveModalOpen, setApproveModalOpen] = useState(false);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedClaim, setSelectedClaim] = useState<Claim | null>(null);
  const [modalLoading, setModalLoading] = useState(false);

  // Load claims
  const loadClaims = useCallback(
    async (page = 1, status: TabStatus = activeTab) => {
      try {
        setLoading(true);
        clearError();

        const params = new URLSearchParams({
          page: page.toString(),
          per_page: '20',
          status: status
        });

        const response = await callApi<ClaimsResponse>({
          method: 'GET',
          endpoint: `/api/admin/claims?${params.toString()}`
        });

        if (response.success) {
          setClaims(response.data.claims || []);
          setTotalClaims(response.data.total || 0);
          setCurrentPage(page);
          const perPage = 20;
          const totalPages = Math.ceil((response.data.total || 0) / perPage);
          setTotalPages(totalPages);
        }
      } catch (error) {
        handleError(error, 'ไม่สามารถโหลดรายการคำขอได้');
        setClaims([]);
      } finally {
        setLoading(false);
      }
    },
    [activeTab, token, callApi, handleError, clearError]
  );

  // Initial load
  useEffect(() => {
    if (token) {
      loadClaims(1, activeTab);
    }
  }, [activeTab, token]);

  // Handle approve claim (from modal)
  const handleApproveConfirm = async (claimId: string, notes?: string) => {
    setModalLoading(true);
    try {
      const requestBody: any = {};
      if (notes) {
        requestBody.notes = notes;
      }

      const response = await callApi({
        method: 'PUT',
        endpoint: `/api/admin/claims/${claimId}/approve`,
        data: requestBody
      });

      if (response.success) {
        toast.success('อนุมัติคำขอเรียบร้อยแล้ว');
        // Reload claims
        await loadClaims(currentPage, activeTab);
      }
    } catch (error) {
      throw error; // Let modal handle the error
    } finally {
      setModalLoading(false);
    }
  };

  // Handle reject claim (from modal)
  const handleRejectConfirm = async (claimId: string, reason: string) => {
    setModalLoading(true);
    try {
      const response = await callApi({
        method: 'PUT',
        endpoint: `/api/admin/claims/${claimId}/reject`,
        data: {
          reason: reason
        }
      });

      if (response.success) {
        toast.success('ปฏิเสธคำขอเรียบร้อยแล้ว');
        // Reload claims
        await loadClaims(currentPage, activeTab);
      }
    } catch (error) {
      throw error; // Let modal handle the error
    } finally {
      setModalLoading(false);
    }
  };

  // Handle view details
  const handleViewDetails = (claim: Claim) => {
    console.log('View details:', claim);
    setSelectedClaim(claim);
    setShowDetailsModal(true);
  };

  // Handle approve click (open modal)
  const handleApproveClick = (claimId: string, companyName: string) => {
    const claim = claims.find((c) => c.id === claimId);
    if (claim) {
      setSelectedClaim(claim);
      setApproveModalOpen(true);
    }
  };

  // Handle reject click (open modal)
  const handleRejectClick = (claimId: string, companyName: string) => {
    const claim = claims.find((c) => c.id === claimId);
    if (claim) {
      setSelectedClaim(claim);
      setRejectModalOpen(true);
    }
  };

  // Get status from claim
  const getClaimStatus = (claim: Claim): TabStatus => {
    if (claim.isApproved && claim.isActive) {
      return 'approved';
    } else if (!claim.isApproved && claim.isActive === false) {
      return 'rejected';
    }
    return 'pending';
  };

  // Filter claims based on search query
  const filteredClaims = claims.filter((claim) => {
    if (!searchQuery) return true;

    const searchLower = searchQuery.toLowerCase();
    const companyName = claim.company?.nameTh?.toLowerCase() || '';
    const userEmail = claim.user?.email?.toLowerCase() || '';
    const userName = `${claim.user?.firstName || ''} ${claim.user?.lastName || ''}`.toLowerCase();

    return (
      companyName.includes(searchLower) ||
      userEmail.includes(searchLower) ||
      userName.includes(searchLower)
    );
  });

  // Count claims by status
  const pendingCount = claims.filter((c) => getClaimStatus(c) === 'pending').length;
  const approvedCount = claims.filter((c) => getClaimStatus(c) === 'approved').length;
  const rejectedCount = claims.filter((c) => getClaimStatus(c) === 'rejected').length;

  return (
    <AdminLayout
      title="จัดการคำขอสิทธิ์"
      breadcrumbs={[
        { label: 'แดชบอร์ด', href: '/admin/dashboard' },
        { label: 'จัดการคำขอสิทธิ์' }
      ]}
    >
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center">
              <FiFileText className="mr-3 h-8 w-8 text-blue-600" />
              จัดการคำขอสิทธิ์
            </h1>
            <p className="mt-1 text-sm text-gray-600">
              จัดการคำขอสิทธิ์การเป็นเจ้าของบริษัท (รวม {totalClaims.toLocaleString()} รายการ)
            </p>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors"
            >
              <FiFilter className="mr-2 h-4 w-4" />
              ตัวกรอง
            </button>
            <button
              onClick={() => loadClaims(currentPage, activeTab)}
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors"
            >
              <FiRefreshCw className="mr-2 h-4 w-4" />
              รีเฟรช
            </button>
          </div>
        </div>

        {/* Search Bar */}
        <div className="relative">
          <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            placeholder="ค้นหาด้วยชื่อบริษัท, อีเมลผู้ใช้, หรือชื่อผู้ใช้..."
            className="pl-10 pr-4 py-3 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent shadow-sm"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        {/* Filters */}
        {showFilters && (
          <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
            <div className="text-sm text-gray-600">
              ตัวกรองเพิ่มเติม (จะพัฒนาในอนาคต)
            </div>
          </div>
        )}

        {/* Tab Navigation */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('pending')}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'pending'
                  ? 'border-amber-500 text-amber-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              รอดำเนินการ
              {pendingCount > 0 && (
                <span className="ml-2 bg-amber-100 text-amber-800 py-0.5 px-2.5 rounded-full text-xs font-medium">
                  {pendingCount}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('approved')}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'approved'
                  ? 'border-green-500 text-green-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              อนุมัติแล้ว
              {approvedCount > 0 && (
                <span className="ml-2 bg-green-100 text-green-800 py-0.5 px-2.5 rounded-full text-xs font-medium">
                  {approvedCount}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('rejected')}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'rejected'
                  ? 'border-red-500 text-red-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              ปฏิเสธแล้ว
              {rejectedCount > 0 && (
                <span className="ml-2 bg-red-100 text-red-800 py-0.5 px-2.5 rounded-full text-xs font-medium">
                  {rejectedCount}
                </span>
              )}
            </button>
          </nav>
        </div>

        {/* Error Display */}
        {errorState.hasError && errorState.error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <FiAlertCircle className="h-5 w-5 text-red-400" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">เกิดข้อผิดพลาด</h3>
                <p className="mt-1 text-sm text-red-700">{errorState.error.message}</p>
              </div>
            </div>
          </div>
        )}

        {/* Claims Grid */}
        <div className="space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <FiRefreshCw className="animate-spin h-8 w-8 text-blue-600" />
              <span className="ml-2 text-gray-600">กำลังโหลด...</span>
            </div>
          ) : filteredClaims.length > 0 ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {filteredClaims.map((claim) => (
                <ClaimCard
                  key={claim.id}
                  claim={claim}
                  onApprove={handleApproveClick}
                  onReject={handleRejectClick}
                  onViewDetails={handleViewDetails}
                  actionLoading={actionLoading === claim.id}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
              <FiFileText className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">ไม่พบคำขอ</h3>
              <p className="mt-1 text-sm text-gray-500">
                {searchQuery
                  ? 'ลองปรับเปลี่ยนคำค้นหา'
                  : `ไม่มีคำขอในสถานะ "${activeTab === 'pending' ? 'รอดำเนินการ' : activeTab === 'approved' ? 'อนุมัติแล้ว' : 'ปฏิเสธแล้ว'}"`}
              </p>
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6 rounded-lg">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => loadClaims(currentPage - 1, activeTab)}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 transition-colors"
              >
                ก่อนหน้า
              </button>
              <button
                onClick={() => loadClaims(currentPage + 1, activeTab)}
                disabled={currentPage === totalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 transition-colors"
              >
                ถัดไป
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  แสดง <span className="font-medium">{(currentPage - 1) * 20 + 1}</span> ถึง{' '}
                  <span className="font-medium">{Math.min(currentPage * 20, totalClaims)}</span> จาก{' '}
                  <span className="font-medium">{totalClaims}</span> รายการ
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    onClick={() => loadClaims(currentPage - 1, activeTab)}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 transition-colors"
                  >
                    ก่อนหน้า
                  </button>
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    const page = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
                    return (
                      <button
                        key={page}
                        onClick={() => loadClaims(page, activeTab)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium transition-colors ${
                          page === currentPage
                            ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                    );
                  })}
                  <button
                    onClick={() => loadClaims(currentPage + 1, activeTab)}
                    disabled={currentPage === totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 transition-colors"
                  >
                    ถัดไป
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Approve Modal */}
      <ApproveClaimModal
        claim={selectedClaim}
        isOpen={approveModalOpen}
        onClose={() => {
          setApproveModalOpen(false);
          setSelectedClaim(null);
        }}
        onConfirm={handleApproveConfirm}
        loading={modalLoading}
      />

      {/* Reject Modal */}
      <RejectClaimModal
        claim={selectedClaim}
        isOpen={rejectModalOpen}
        onClose={() => {
          setRejectModalOpen(false);
          setSelectedClaim(null);
        }}
        onConfirm={handleRejectConfirm}
        loading={modalLoading}
      />

      {/* View Details Modal */}
      <ViewClaimDetailsModal
        claim={selectedClaim}
        isOpen={showDetailsModal}
        onClose={() => {
          setShowDetailsModal(false);
          setSelectedClaim(null);
        }}
      />
    </AdminLayout>
  );
};

export default RequestsPage;
